import {ClientUserSessionState} from '../../../../shared/ClientUserSessionState.ts'
import {globalClientVariables, notificationController, socket} from '../EntryShared.ts'
import {NetworkEnums} from '../../../../shared/GameEnums.ts'
import {type RoomClientState} from '../RoomClientState.ts'
import {type SocketDataTypes} from '../../../../shared/SocketDataTypes.ts'
import {UIPopupController} from '../ui_popup/UIPopupController.ts'
import {ClientSocketMessageLoadTest} from './ClientSocketMessageLoadTest.ts'
import {UIDocument} from '../UIDocument.ts'
import {type SocketActionController} from './SocketActionController.ts'
import {UITopNotificationType} from '../notifications/UINotificationController.ts'
import LobbyStateUpdateData = NetworkEnums.LobbyStateUpdateData
import ChannelStateUpdateData = NetworkEnums.ChannelStateUpdateData
import GeneralStateUpdateData = NetworkEnums.GeneralStateUpdateData
import {type BigId, COLONIST_VERSION} from '@colonist/types'
import {ViewPopupServerUpdate} from '../ui_popup/ViewPopupServerUpdate.ts'
import {getMajorVersion} from '@colonist/utils'
import {type TranslationObject} from '@colonist/i18n'
import {ClientUserState} from '../ClientUserState.ts'
import {type FriendRequestReceivedData, type FriendRoomInviteNotificationData, type RoomInviteResponse, type UpdateOnlineStatusData} from '../../../../shared/friends/FriendTypes.ts'
import {type MarkedAsReadData, type NotificationDataWithUnreadCount} from '../../../../shared/NotificationTypes.ts'
import {type UserStateData} from '../../../../shared/UserStateData.ts'
import type {SocketController} from './SocketController.ts'
import {SocketShuffleSend} from './SocketShuffleSend.ts'
import {ViewPopupSuccessfulPurchase} from '../ui_popup/ViewPopupSuccessfulPurchase.ts'
import {type BaseStoreItem} from '../../../../shared/store/BaseStoreItem.ts'

export class SocketLobbyListen {
    private readonly socketActionController: SocketActionController
    /** @deprecated */
    room: RoomClientState
    // eslint-disable-next-line func-call-spacing
    private readonly lobbyStateHandlers = new Map<NetworkEnums.LobbyState, (data: any) => void>()
    // eslint-disable-next-line func-call-spacing
    private readonly channelStateHandlers = new Map<NetworkEnums.ChannelState, (data: any) => void>()
    // eslint-disable-next-line func-call-spacing
    private readonly generalStateHandlers = new Map<NetworkEnums.GeneralState, (data: any) => void>()

    constructor(socketController: SocketController, socketActionController: SocketActionController) {
        this.socketActionController = socketActionController

        socketController.setStateHandler(NetworkEnums.State.LobbyStateUpdate, (data: LobbyStateUpdateData) => {
            this.lobbyStateHandlers.get(data.type)?.(data.payload)
        })

        socketController.setStateHandler(NetworkEnums.State.ChannelStateUpdate, (data: ChannelStateUpdateData) => {
            this.channelStateHandlers.get(data.type)?.(data.payload)
        })

        socketController.setStateHandler(NetworkEnums.State.GeneralStateUpdate, (data: GeneralStateUpdateData) => {
            this.generalStateHandlers.get(data.type)?.(data.payload)
        })

        this.lobbyStateHandlers.set(NetworkEnums.LobbyState.SessionState, (data: ClientUserSessionState) => {
            const session = ClientUserSessionState.deserialize(data)
            UIDocument.monitorIfUserInteractedWithSite(ClientUserState.userState?.interactedWithSite)
            socketController.setSession(session)
            this.socketActionController.onStateSessionState()

            SocketShuffleSend.requestShuffleQueueData()
        })

        this.lobbyStateHandlers.set(NetworkEnums.LobbyState.LobbyDisconnectData, (data: SocketDataTypes.UserGamesData[]) => {
            notificationController.showGames(data)
        })

        this.lobbyStateHandlers.set(NetworkEnums.LobbyState.AutoRejoinGame, (gameId: string) => {
            this.socketActionController.onStateAutoRejoinGame(gameId)
        })

        this.lobbyStateHandlers.set(NetworkEnums.LobbyState.SendPopup, (data: SocketDataTypes.SimplePopupData) => {
            if(data.body.key == 'strings:popups.errors.newVersion.refreshClient') {
                UIPopupController.createForceRefreshPopup(data.title, data.body)
            } else {
                UIPopupController.createSimplePopup(data.title, data.body)
            }
        })

        this.lobbyStateHandlers.set(NetworkEnums.LobbyState.SendBroadcast, (data: SocketDataTypes.BroadcastData) => {
            notificationController.show(data.text, UITopNotificationType.Information)
        })

        this.lobbyStateHandlers.set(NetworkEnums.LobbyState.SocketTest, (data: any) => {
            ClientSocketMessageLoadTest.processSocketData(data)
        })

        this.lobbyStateHandlers.set(NetworkEnums.LobbyState.ShowCornerPopup, (data: SocketDataTypes.NotificationData) => {
            notificationController.showNotification(data.icon, data.text)
        })

        this.lobbyStateHandlers.set(NetworkEnums.LobbyState.ServerUpdating, () => {
            const popup = new ViewPopupServerUpdate()
            popup.show()
        })

        this.lobbyStateHandlers.set(NetworkEnums.LobbyState.ReloadClient, (data: NetworkEnums.ClientReloadData) => {
            setTimeout(() => window.location.reload(), data.delay)
        })

        this.lobbyStateHandlers.set(NetworkEnums.LobbyState.ServerUpdated, (data: NetworkEnums.ServerUpdatedData) => {
            if(socket.gameListener.databaseGameId == undefined) return
            if(getMajorVersion(COLONIST_VERSION) == getMajorVersion(data.newServerVersion)) return

            const updateMessage: TranslationObject = {
                key: 'strings:popups.errors.newVersion.newVersionReleasedWarning',
            }
            notificationController.show(updateMessage, UITopNotificationType.Information)
            globalClientVariables.serverUpdated = true
        })

        this.lobbyStateHandlers.set(NetworkEnums.LobbyState.RoomListUpdate, (data: NetworkEnums.RoomListUpdateData) => {
            this.socketActionController.onRoomListUpdate(data)
        })

        this.lobbyStateHandlers.set(NetworkEnums.LobbyState.UserStateUpdate, (data: UserStateData) => {
            ClientUserState.update(data)
        })

        this.lobbyStateHandlers.set(NetworkEnums.LobbyState.ItemPurchaseSuccessPopup, (data: BaseStoreItem) => {
            new ViewPopupSuccessfulPurchase(data)
        })

        this.channelStateHandlers.set(NetworkEnums.ChannelState.ChannelRoomUpdate, (data: SocketDataTypes.ChannelRoomUpdate) => {
            this.socketActionController.onChannelRoomUpdate(data)
        })

        this.generalStateHandlers.set(NetworkEnums.GeneralState.UpdateOnlineStatus, (data: UpdateOnlineStatusData) => {
            this.socketActionController.onFriendUpdateOnlineStatus(data)
        })

        this.generalStateHandlers.set(NetworkEnums.GeneralState.ReceivedRoomInvites, (data: FriendRoomInviteNotificationData[]) => {
            this.socketActionController.onFriendReceivedRoomInvites(data)
        })

        this.generalStateHandlers.set(NetworkEnums.GeneralState.ReceivedFriendRequest, (data: FriendRequestReceivedData) => {
            this.socketActionController.onFriendReceivedFriendRequest(data)
        })

        this.generalStateHandlers.set(NetworkEnums.GeneralState.RemoveRoomInvite, (roomId: string) => {
            this.socketActionController.onFriendRemoveRoomInvite(roomId)
        })

        this.generalStateHandlers.set(NetworkEnums.GeneralState.RoomInviteResponse, (data: RoomInviteResponse) => {
            this.socketActionController.onFriendRoomInviteResponse(data)
        })

        this.generalStateHandlers.set(NetworkEnums.GeneralState.RoomInviteSentSuccessfully, (receiverUserId: BigId) => {
            this.socketActionController.onFriendRoomInviteSentSuccessfully(receiverUserId)
        })

        this.generalStateHandlers.set(NetworkEnums.GeneralState.Notifications, (notificationDataWithUnreadCount: NotificationDataWithUnreadCount) => {
            this.socketActionController.onNotificationsReceived(notificationDataWithUnreadCount)
        })

        this.generalStateHandlers.set(NetworkEnums.GeneralState.MarkedAsRead, (markedAsReadData: MarkedAsReadData) => {
            this.socketActionController.onMarkedAsRead(markedAsReadData)
        })
    }
}
